import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Chip,
  Grid,
  Alert
} from '@mui/material';
import {
  Close,
  CheckCircle,
  Cancel,
  AccessTime,
  Build,
  Person,
  Email
} from '@mui/icons-material';
import uploadService from '../services/uploadService';
import ApproveOrderDialog from './ApproveOrderDialog';
import RejectOrderDialog from './RejectOrderDialog';

const STATUS_CONFIG = {
  pendiente: {
    color: 'warning',
    icon: <AccessTime />,
    label: 'Pendiente',
    description: 'Esperando ser procesado'
  },
  procesando: {
    color: 'info',
    icon: <Build />,
    label: 'En Proceso',
    description: 'Siendo revisado'
  },
  aprobado: {
    color: 'success',
    icon: <CheckCircle />,
    label: 'Aprobado',
    description: 'Convertido en venta'
  },
  rechazado: {
    color: 'error',
    icon: <Cancel />,
    label: 'Rechazado',
    description: 'No aprobado'
  },
  cancelado: {
    color: 'default',
    icon: <Cancel />,
    label: 'Cancelado',
    description: 'Cancelado por el cliente'
  }
};

const OrderDetailDialog = ({ open, onClose, order, onOrderUpdated }) => {
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);

  if (!order) return null;

  const statusInfo = STATUS_CONFIG[order.status] || STATUS_CONFIG.pendiente;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(price || 0);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const canApprove = order.status === 'pendiente' || order.status === 'procesando';
  const canReject = order.status === 'pendiente' || order.status === 'procesando';

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">
                Pedido #{order.orderNumber}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Creado el {formatDate(order.createdAt)}
              </Typography>
            </Box>
            <Chip
              icon={statusInfo.icon}
              label={statusInfo.label}
              color={statusInfo.color}
              size="small"
            />
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* Información del Cliente */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person fontSize="small" />
              Información del Cliente
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Nombre:
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                  {order.customer?.fullName || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Email fontSize="small" />
                  Email:
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                  {order.customer?.email || order.customer?.username || 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Productos */}
          <Typography variant="subtitle2" gutterBottom>
            Productos del Pedido:
          </Typography>
          <List sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 2 }}>
            {order.items?.map((item, index) => (
              <React.Fragment key={item.id}>
                <ListItem alignItems="flex-start">
                  <ListItemAvatar>
                    <Avatar
                      variant="rounded"
                      src={item.product?.image ? uploadService.getImageUrl(item.product.image) : '/no-image.svg'}
                      alt={item.productName}
                      sx={{ width: 56, height: 56 }}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {item.productName}
                      </Typography>
                    }
                    secondary={
                      <Box component="div" sx={{ mt: 0.5 }}>
                        {item.productCode && (
                          <Typography variant="caption" display="block" color="text.secondary" component="span">
                            Código: {item.productCode}
                          </Typography>
                        )}
                        <Typography variant="body2" sx={{ mt: 0.5 }} component="div">
                          Cantidad: <strong>{item.quantity}</strong> x {formatPrice(item.unitPrice)}
                        </Typography>
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 600, mt: 0.5 }} component="div">
                          Subtotal: {formatPrice(item.subtotal)}
                        </Typography>
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItem>
                {index < order.items.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>

          {/* Total */}
          <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 1, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Total del Pedido:
              </Typography>
              <Typography variant="h5" color="primary" sx={{ fontWeight: 700 }}>
                {formatPrice(order.total)}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {order.items?.reduce((sum, item) => sum + item.quantity, 0)} producto(s) en total
            </Typography>
          </Box>

          {/* Notas del Cliente */}
          {order.notes && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Notas del Cliente:
              </Typography>
              <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="body2">
                  {order.notes}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Notas del Admin (si existen) */}
          {order.adminNotes && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Notas Internas:
              </Typography>
              <Box sx={{ p: 2, bgcolor: 'info.lighter', borderRadius: 1 }}>
                <Typography variant="body2">
                  {order.adminNotes}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Motivo de Rechazo */}
          {order.rejectionReason && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Motivo de Rechazo:
              </Typography>
              <Typography variant="body2">
                {order.rejectionReason}
              </Typography>
            </Alert>
          )}

          {/* Información de Procesamiento */}
          {(order.processedBy || order.processedAt) && (
            <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Procesado por: {order.processor?.fullName || 'N/A'}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Fecha de procesamiento: {formatDate(order.processedAt)}
              </Typography>
              {order.saleId && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Venta asociada: #{order.saleId}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={onClose}
            startIcon={<Close />}
          >
            Cerrar
          </Button>

          {canReject && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<Cancel />}
              onClick={() => setRejectDialogOpen(true)}
            >
              Rechazar
            </Button>
          )}

          {canApprove && (
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
              onClick={() => setApproveDialogOpen(true)}
            >
              Aprobar y Procesar
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog de Aprobación */}
      <ApproveOrderDialog
        open={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
        order={order}
        onSuccess={() => {
          setApproveDialogOpen(false);
          onOrderUpdated();
        }}
      />

      {/* Dialog de Rechazo */}
      <RejectOrderDialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        order={order}
        onSuccess={() => {
          setRejectDialogOpen(false);
          onOrderUpdated();
        }}
      />
    </>
  );
};

export default OrderDetailDialog;

